import { useEffect, useState } from "react";
import { api, fmtEUR } from "@/lib/api";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DecimalField } from "@/components/DecimalInput";
import { intOpts, parseDecimal } from "@/lib/decimal";
import { counts, isPart, bezeichnung, datei } from "@/lib/produktion";

// Startwerte: Menge = fertig, aber noch nicht versendet; beiliegende Kundenteile leer.
export function initialInhalt(order) {
  return {
    mengen: Object.fromEntries((order?.positionen || []).filter(isPart).map((p) => [p.id, String(counts(p).fertig)])),
    kundenteile: [], versandkosten: false, versandartId: "", zusatzOn: false, zusatz: {}, text: "",
  };
}

const ZUSATZ_MAX = 10000;
export const GRUND_FEHLT = "Bitte „Inhalt / Grund“ angeben – das Paket enthält keine Positionen und keine Kundenteile.";
export const VERSANDKOSTEN_RECHNUNG = "Rechnung bereits erstellt – Versandkosten können nicht mehr berechnet werden";

// → { error, body } für POST /versand
export function inhaltBody(order, v) {
  const inhalt = [], zusatz = [];
  const parts = (order?.positionen || []).filter(isPart);
  for (const p of parts) {
    const r = parseDecimal(v.mengen[p.id] ?? "0", intOpts(0, counts(p).unversendet));
    if (r.error) return { error: `${bezeichnung(p)}: ${r.error}` };
    if (r.value > 0) inhalt.push({ position_id: p.id, menge: r.value });
  }
  if (v.zusatzOn) {
    for (const p of parts) {
      const r = parseDecimal(v.zusatz[p.id] ?? "0", intOpts(0, ZUSATZ_MAX));
      if (r.error) return { error: `Zusätzliche Menge ${bezeichnung(p)}: ${r.error}` };
      if (r.value > 0) zusatz.push({ position_id: p.id, menge: r.value });
    }
  }
  const text = (v.text || "").trim();
  if (!inhalt.length && !zusatz.length && !v.kundenteile.length && !text) return { error: GRUND_FEHLT };
  const vk = v.versandkosten && !order?.rechnungsnummer;
  if (vk && !v.versandartId) return { error: "Bitte eine Versandart für die Versandkosten wählen." };
  return { body: { inhalt, zusatz, inhalt_text: text, kundenteile: v.kundenteile, versandkosten_berechnen: vk, versandart_id: vk ? v.versandartId : null } };
}

function InhaltRow({ p, idx, value, onChange }) {
  const c = counts(p);
  const r = parseDecimal(value, intOpts(0, c.unversendet));
  const warn = !r.error && r.value > c.fertig;
  return (
    <li className="py-2 flex items-start gap-3" data-testid={`paket-pos-${idx}`}>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">{bezeichnung(p)}</div>
        <div className="text-[11px] text-slate-500">{datei(p) && `${datei(p)} · `}fertig {c.fertig} · noch nicht versendet {c.unversendet} · versendet {c.versendet}</div>
        {warn && <div className="text-[11px] text-amber-700" data-testid={`paket-pos-warn-${idx}`}>Mehr als fertig gemeldet ({c.fertig}) – trotzdem möglich.</div>}
      </div>
      <DecimalField integer className="h-8 w-20" value={value} onChange={onChange} error={r.error} aria-label={`Menge ${bezeichnung(p)}`} testId={`paket-menge-${idx}`} />
    </li>
  );
}

// Zusätzliche Menge über die bestellte Menge hinaus (Ersatz/Nachlieferung) – ändert keine Produktionszahlen
function ZusatzMengen({ order, value, set }) {
  const parts = (order.positionen || []).filter(isPart);
  return (
    <div className="pt-2 border-t border-slate-200 space-y-1">
      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <Checkbox checked={value.zusatzOn} onCheckedChange={(v) => set({ zusatzOn: !!v })} data-testid="paket-zusatz-checkbox" /> Zusätzliche Menge (Ersatz/Nachlieferung)
      </label>
      {value.zusatzOn && (
        <>
          <p className="text-[11px] text-slate-500">Wird als „zusätzlich“ auf Packzettel und Versandmitteilung ausgewiesen – Bestellmenge und Produktionszahlen bleiben unverändert.</p>
          <ul className="divide-y divide-slate-200" data-testid="paket-zusatz-list">{parts.map((p, i) => {
            const r = parseDecimal(value.zusatz[p.id] ?? "0", intOpts(0, ZUSATZ_MAX));
            return (
              <li key={p.id} className="py-1.5 flex items-center gap-3">
                <div className="flex-1 min-w-0 text-sm truncate">{bezeichnung(p)}<span className="text-[11px] text-slate-500"> · bestellt {p.menge}, versendet {counts(p).versendet}</span></div>
                <DecimalField integer className="h-8 w-20" value={value.zusatz[p.id] ?? "0"} onChange={(t) => set({ zusatz: { ...value.zusatz, [p.id]: t } })} error={r.error}
                  aria-label={`Zusätzliche Menge ${bezeichnung(p)}`} testId={`paket-zusatz-menge-${i}`} />
              </li>
            );
          })}</ul>
        </>
      )}
    </div>
  );
}

export function PaketInhalt({ order, folgepaket, value, onChange }) {
  const [arten, setArten] = useState([]);
  useEffect(() => { if (folgepaket) api.get("/versandarten").then((r) => setArten(r.data.filter((a) => a.aktiv))).catch(() => {}); }, [folgepaket]);
  const parts = (order.positionen || []).filter((p) => isPart(p) && counts(p).unversendet > 0);
  const teile = (order.kundenteile || []).filter((t) => t.muss_zurueck && !t.zurueck);
  const set = (patch) => onChange({ ...value, ...patch });
  const toggleTeil = (id, on) => set({ kundenteile: on ? [...value.kundenteile, id] : value.kundenteile.filter((x) => x !== id) });
  const hatRechnung = !!order.rechnungsnummer;
  return (
    <div className="space-y-2 p-3 border rounded-md bg-slate-50" data-testid="paket-inhalt">
      <div className="text-xs uppercase tracking-wider text-slate-500">Inhalt dieses Pakets</div>
      {parts.length === 0 ? (
        <p className="text-sm p-2 rounded border border-sky-200 bg-sky-50 text-sky-900" data-testid="paket-alles-versendet">Alle Positionen sind bereits versendet – zusätzliches Paket</p>
      ) : (
        <ul className="divide-y divide-slate-200">{parts.map((p, i) => <InhaltRow key={p.id} p={p} idx={i} value={value.mengen[p.id] ?? "0"} onChange={(t) => set({ mengen: { ...value.mengen, [p.id]: t } })} />)}</ul>
      )}
      {teile.length > 0 && (
        <div className="pt-1 space-y-1">
          <div className="text-xs text-slate-500">Kundenteile (müssen zurück)</div>
          {teile.map((t, i) => (
            <label key={t.id} className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox checked={value.kundenteile.includes(t.id)} onCheckedChange={(v) => toggleTeil(t.id, !!v)} data-testid={`paket-kundenteil-${i}`} /> {t.bezeichnung} ({t.anzahl} Stück) liegt bei
            </label>
          ))}
        </div>
      )}
      <ZusatzMengen order={order} value={value} set={set} />
      <div className="pt-2 border-t border-slate-200">
        <Label htmlFor="paket-inhalt-text" className="text-sm">Inhalt / Grund{parts.length === 0 && <span className="text-rose-600"> *</span>}</Label>
        <Textarea id="paket-inhalt-text" rows={2} maxLength={1000} value={value.text} onChange={(e) => set({ text: e.target.value })}
          placeholder="z. B. Ersatzteil, Nachlieferung Schrauben, Unterlagen" data-testid="paket-inhalt-text" />
        <p className="text-[11px] text-slate-500 mt-0.5">Erscheint auf dem Packzettel, im Versandverlauf und in der Versandmitteilung. Pflicht, wenn das Paket keine Positionen und keine Kundenteile enthält.</p>
      </div>
      {folgepaket && (
        <div className="pt-2 border-t border-slate-200 space-y-2">
          <label className={`flex items-center gap-2 text-sm ${hatRechnung ? "text-slate-400" : "cursor-pointer"}`}>
            <Switch checked={value.versandkosten && !hatRechnung} disabled={hatRechnung} onCheckedChange={(v) => set({ versandkosten: v })} data-testid="paket-versandkosten-switch" /> Versandkosten für dieses Paket berechnen
          </label>
          {hatRechnung && <p className="text-[11px] text-slate-500" data-testid="paket-versandkosten-hint">{VERSANDKOSTEN_RECHNUNG} ({order.rechnungsnummer}).</p>}
          {value.versandkosten && !hatRechnung && (
            <div><Label className="text-xs">Versandart (zusätzliche Versandposition)</Label>
              <Select value={value.versandartId} onValueChange={(v) => set({ versandartId: v })}>
                <SelectTrigger data-testid="paket-versandart-select"><SelectValue placeholder="Versandart wählen" /></SelectTrigger>
                <SelectContent>{arten.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} · {fmtEUR(a.brutto)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
